import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button"; // Assuming shadcn/ui Button
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"; // Assuming shadcn/ui Card
import { Badge } from "@/components/ui/badge";
import {
  ThumbsUp,
  ThumbsDown,
  Building,
  DollarSign,
  MapPin,
  Clock,
  Stethoscope,
  Loader2,
  AlertCircle,
  Share2,
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  ClipboardList,
  ShieldCheck,
  Star,
  FileText,
} from "lucide-react";
import { RenderHtml } from "@/components/RenderHtml"; // Ensure this component exists
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Link } from "@tanstack/react-router";
import { queryClient } from "@/ajax/queryClient";
import {
  showNotInterestedToast,
  showNotInterestedErrorToast,
} from "./utils/show-not-interested-toast";
import { JobAcceptDialog } from "./job-accept-dialog";
import { JobPostRemoveInterestDialog } from "./job-post-remove-interest-dialog";
import { ExternalJobBadge } from "./components/external-job-badge";
import { EligibilityBadge } from "./components/eligibility-badge";
import { ProviderHiringProcessView } from "@/features/hiring-process/provider-hiring-process-view";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useReferralCode } from "@/features/referrals/useReferralCode";
import { getJobShareUrl } from "./utils/getJobShareUrl";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { getSalaryRange } from "./utils/get-salary-range";
import { useMarkJobPostViewed } from "./use-mark-job-post-viewed";
import { JobUnavailableState } from "./job-unavailable-state";
import { MatchStrengthBadge } from "./components/match-strength-badge";

// --- JobPage Component ---

interface JobPageProps {
  jobId: string;
  sourceUserId?: string | null;
  onDecisionAccepted?: () => void;
}

export function JobDetail({
  jobId,
  sourceUserId,
  onDecisionAccepted,
}: JobPageProps) {
  const trpc = useTRPC();
  const {
    data: job,
    isLoading,
    error,
    refetch: refetchJob,
  } = useQuery(trpc.jobPost.get.queryOptions({ id: jobId }));
  const markViewed = useMarkJobPostViewed();
  const isJobLoaded = !!job;
  // Opening the page counts as viewing, but only if the job actually loaded.
  useEffect(() => {
    if (isJobLoaded) {
      markViewed(jobId);
    }
  }, [isJobLoaded, jobId, markViewed]);
  const { mutateAsync: setDecision } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.jobPost.get.queryKey({ id: jobId }),
        });
      },
    }),
  );
  const [interestLoading, setInterestLoading] = useState<boolean | null>(null);
  const [acceptedJobId, setAcceptedJobId] = useState<string | null>(null);
  const [acceptedJobExternalUrl, setAcceptedJobExternalUrl] = useState<
    string | null
  >(null);
  const [removeInterestJobId, setRemoveInterestJobId] = useState<string | null>(
    null,
  );
  const [removeInterestPreviousDecision, setRemoveInterestPreviousDecision] =
    useState<boolean | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const referralCode = useReferralCode();
  const jobShareUrl = useMemo(
    () => getJobShareUrl(job?.id ?? jobId, referralCode),
    [job?.id, jobId, referralCode],
  );

  const handleInterestChange = async (
    jobId: string,
    isInterested: boolean | null,
  ) => {
    if (isInterested === false) {
      try {
        await setDecision({ id: jobId, interested: false });
        showNotInterestedToast();
      } catch {
        showNotInterestedErrorToast();
      }
      return;
    }
    if (isInterested === true) {
      setAcceptedJobId(jobId);
      setAcceptedJobExternalUrl(job?.externalUrl ?? null);
      return;
    }
    if (isInterested === null) {
      setRemoveInterestJobId(jobId);
      setRemoveInterestPreviousDecision(job?.decision?.interested ?? null);
      return;
    }
  };

  // --- Event Handlers ---
  const handleInterest = async (isInterested: boolean) => {
    if (!job) {
      return;
    } // Should not happen if button is enabled, but safety first

    let newStatus: boolean | null = isInterested;
    // If clicking the same button again, toggle it off (set to null)
    if (job.decision?.interested === isInterested) {
      newStatus = null;
    }
    setInterestLoading(newStatus); // Indicate loading for the specific action
    try {
      await handleInterestChange(job.id, newStatus);
      // Optionally update local job state if the parent doesn't trigger a re-render with new props
      // setJob(prevJob => prevJob ? { ...prevJob, decision: { interested: newStatus } } : null);
    } catch (err) {
      console.error("Failed to update interest:", err);
      // Handle error display if needed
    } finally {
      setInterestLoading(null); // Stop loading indicator
    }
  };

  const handleShare = async () => {
    if (!job) {
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        const shareTitle = job.title || "Job Opportunity";
        const shareText = `Check out this job: ${shareTitle}${
          facilityName ? ` at ${facilityName}` : ""
        }${location ? ` in ${location}` : ""}. ${jobShareUrl}`;

        await Share.share({
          title: shareTitle,
          text: shareText,
          dialogTitle: "Share this job",
          url: jobShareUrl,
        });
      } else {
        setIsShareDialogOpen(true);
      }
    } catch (error) {
      console.error("Error sharing job:", error);
    }
  };

  const handleCopyLink = async () => {
    if (!job) {
      return;
    }

    try {
      await navigator.clipboard.writeText(jobShareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Error copying link:", error);
    }
  };

  const salaryRange = job ? getSalaryRange(job) : "";

  const location = useMemo(() => {
    if (!job) {
      return "";
    }
    if (job.city) {
      return job.stateCode ? `${job.city}, ${job.stateCode}` : job.city;
    }
    return job.stateCode || "";
  }, [job?.city, job?.stateCode]);

  const facilityName = useMemo(() => {
    if (!job) {
      return "";
    }
    return job?.facility?.name || job.facilityName || "";
  }, [job?.facility?.name, job?.facilityName]);

  // --- Render Logic ---

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-4">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-red-700 mb-2">
          Error Loading Job
        </h2>
      </div>
    );
  }

  if (!job) {
    return (
      <JobUnavailableState
        jobId={jobId}
        onReopened={refetchJob}
        actions={
          <>
            <Button asChild size="lg">
              <Link to="/provider/jobs/search">Find similar jobs</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/provider/jobs/recommended">See recommended jobs</Link>
            </Button>
          </>
        }
      />
    );
  }

  // Main Job Content - Displayed when job data is available
  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8 space-y-6">
      {/* Back to Jobs Link */}
      <Link
        to="/provider/jobs"
        className="inline-flex items-center rounded-md text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" />
        Back to Job Portal
      </Link>

      {/* --- Header Section --- */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Title & Actions Bar */}
        <div className="p-6 pb-0">
          <div className="flex flex-col items-start gap-4">
            <div className="flex flex-col items-start gap-2.5">
              <CardTitle className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight grow">
                {job.title || "Job Title Not Available"}
              </CardTitle>
              <div className="empty:hidden">
                <MatchStrengthBadge match={job.match} />
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex w-full flex-wrap items-center gap-2">
              <Button
                variant="outline"
                disabled={interestLoading === false || interestLoading === true}
                aria-label="Hide job"
                aria-pressed={job.decision?.interested === false}
                onClick={() => handleInterest(false)}
                className={`gap-2 px-3 ${
                  job.decision?.interested === false
                    ? "bg-slate-800 border-slate-800 text-white hover:bg-slate-700 hover:text-white"
                    : ""
                }`}
              >
                {interestLoading === false ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ThumbsDown
                    className={`h-4 w-4 ${job.decision?.interested === false ? "fill-current" : ""}`}
                    aria-hidden="true"
                  />
                )}
                <span className="hidden sm:inline">
                  {job.decision?.interested === false ? "Hidden" : "Hide"}
                </span>
              </Button>
              <Button
                variant="outline"
                onClick={handleShare}
                className="h-12 w-12 shrink-0 px-0"
                aria-label="Share Job"
              >
                <Share2 className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                disabled={interestLoading === true || interestLoading === false}
                onClick={() => handleInterest(true)}
                aria-label="Interested"
                aria-pressed={job.decision?.interested === true}
                className={`gap-2 border px-3 ${
                  job.decision?.interested === true
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                    : "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
                }`}
              >
                {interestLoading === true ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ThumbsUp
                    className={`h-4 w-4 ${job.decision?.interested === true ? "fill-current" : ""}`}
                    aria-hidden="true"
                  />
                )}
                {job.decision?.interested === true ? "Applied" : "Apply"}
              </Button>
              {job.externalUrl && job.decision?.interested === true && (
                <Button
                  onClick={() => {
                    setAcceptedJobId(job.id);
                    setAcceptedJobExternalUrl(job.externalUrl ?? null);
                  }}
                  className="gap-2 px-3"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Application
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Badges Row */}
        <div className="px-6 pt-4">
          <div className="flex flex-wrap gap-2">
            {salaryRange && (
              <Badge
                variant="outline"
                className="bg-primary/5 border-primary/15 text-primary rounded-full px-3 py-1 text-sm font-medium"
              >
                <DollarSign className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                {salaryRange}
              </Badge>
            )}
            {(job.durationIds ?? []).map((durationId) => (
              <Badge
                key={durationId}
                variant="outline"
                className="bg-slate-50 border-slate-200 text-slate-700 rounded-full px-3 py-1 text-sm font-medium"
              >
                {getJobDurationLabel(durationId)}
              </Badge>
            ))}
            {job.facilityType && (
              <Badge
                variant="outline"
                className="rounded-full px-3 py-1 text-sm font-medium bg-slate-50 border-slate-200 text-slate-600"
              >
                {job.facilityType.name}
              </Badge>
            )}
            {job.externalUrl && <ExternalJobBadge />}
            <EligibilityBadge eligibility={job.eligibility} />
          </div>
        </div>

        {/* Details Grid */}
        <div className="px-6 py-5 space-y-3">
          {/* Professional Type & Specialty */}
          {job.healthcareProfessionalTypes &&
            job.healthcareProfessionalTypes.length > 0 && (
              <div className="flex items-start gap-2.5">
                <Stethoscope className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <div className="text-sm">
                  <span className="text-slate-500">
                    {job.healthcareProfessionalTypes
                      .map((type) => type.name)
                      .join(", ")}
                    :
                  </span>{" "}
                  <span className="font-medium text-slate-800">
                    {job.specialties && job.specialties.length > 0 ? (
                      job.specialties.map((s) => s.name).join(", ")
                    ) : (
                      <span className="text-slate-400 italic">
                        Not specified
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

          {/* Facility */}
          {facilityName && (
            <div className="flex items-center gap-2.5 text-sm">
              <Building className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="text-slate-700">
                {facilityName}{" "}
                {job?.facilityType ? `(${job.facilityType.name})` : ""}
              </span>
            </div>
          )}

          {/* Location */}
          {location && (
            <div className="flex items-center gap-2.5 text-sm">
              <MapPin className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="text-slate-700">{location}</span>
            </div>
          )}
        </div>
      </div>

      {/* Checklist view — shown when provider has expressed interest. The
          view itself returns null if the provider is rejected or has no
          active hiring process, so we don't need to gate on rejection here. */}
      {job.decision?.interested === true && (
        <ProviderHiringProcessView jobPostId={jobId} />
      )}

      {/* --- Detailed Sections --- */}

      {/* Schedule Description */}
      {job.scheduleDescription && (
        <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
              Work Schedule Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6 text-sm text-slate-600 leading-relaxed">
            <RenderHtml html={job.scheduleDescription} />
          </CardContent>
        </Card>
      )}

      {/* Responsibilities */}
      {job.responsibilities && job.responsibilities.length > 0 && (
        <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <ClipboardList
                className="h-4 w-4 text-primary"
                aria-hidden="true"
              />
              Responsibilities
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6">
            <ul className="space-y-2.5">
              {job.responsibilities.map((item: string, index: number) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm text-slate-600"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Required Qualifications */}
      {job.requiredQualifications && job.requiredQualifications.length > 0 && (
        <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck
                className="h-4 w-4 text-primary"
                aria-hidden="true"
              />
              Required Qualifications
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6">
            <ul className="space-y-2.5">
              {job.requiredQualifications.map((item: string, index: number) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm text-slate-600"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Preferred Qualifications */}
      {job.preferredQualifications &&
        job.preferredQualifications.length > 0 && (
          <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Star className="h-4 w-4 text-primary" aria-hidden="true" />
                Preferred Qualifications
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-6 pb-6">
              <ul className="space-y-2.5">
                {job.preferredQualifications.map(
                  (item: string, index: number) => (
                    <li
                      key={index}
                      className="flex items-start gap-2.5 text-sm text-slate-600"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                      {item}
                    </li>
                  ),
                )}
              </ul>
            </CardContent>
          </Card>
        )}

      {/* Job Description */}
      <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
            Job Description
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-6 pb-6 text-sm text-slate-600 prose prose-sm max-w-none">
          {job.description ? (
            <RenderHtml html={job.description} />
          ) : (
            <p className="text-slate-400 italic flex items-center gap-2">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              Job description not provided.
            </p>
          )}
        </CardContent>
      </Card>

      {acceptedJobId && job && (
        <JobAcceptDialog
          job={{
            id: acceptedJobId,
            externalUrl: acceptedJobExternalUrl,
            eligibility: job.eligibility,
          }}
          onClose={() => {
            setAcceptedJobId(null);
            setAcceptedJobExternalUrl(null);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries(
              trpc.jobPost.get.queryOptions({ id: jobId }),
            );
            if (!acceptedJobExternalUrl) {
              onDecisionAccepted?.();
            }
          }}
          sourceUserId={sourceUserId}
          alreadyLiked={
            acceptedJobExternalUrl
              ? job.decision?.interested === true
              : undefined
          }
        />
      )}
      {removeInterestJobId && (
        <JobPostRemoveInterestDialog
          onClose={() => {
            setRemoveInterestJobId(null);
            setRemoveInterestPreviousDecision(null);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries(
              trpc.jobPost.get.queryOptions({ id: jobId }),
            );
          }}
          jobId={removeInterestJobId}
          previousDecision={removeInterestPreviousDecision ?? undefined}
        />
      )}

      {/* Share Dialog for Web Browsers */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-md sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle>Share this job</DialogTitle>
            <DialogDescription className="text-slate-600">
              Copy the link below to share this job opportunity
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 mt-4">
            <div className="grid min-w-0 flex-1 gap-2">
              <div className="flex items-center">
                <Input
                  className="text-sm text-slate-700 truncate flex-1"
                  aria-label="Job share link"
                  value={jobShareUrl}
                  readOnly
                />
              </div>
            </div>
            <Button
              onClick={handleCopyLink}
              className="shrink-0 px-3"
              variant="outline"
            >
              {isCopied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              <span className="sr-only">Copy</span>
            </Button>
          </div>
          <div className="mt-4">
            <Button
              onClick={() => setIsShareDialogOpen(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
